import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, RotateCcw, BookOpen, Volume2, Sparkles, CheckCircle2 } from 'lucide-react';
import { apiCall, speak } from './api';

/** Révisions : mode SRS (cartes, répétition espacée) + relecture des leçons récentes. */
export default function Review({ token, onBack, onOpenLesson }) {
  const [items, setItems] = useState([]);
  const [due, setDue] = useState([]);
  const [stats, setStats] = useState({ total: 0, due: 0, learned: 0 });
  const [mode, setMode] = useState('home'); // home | srs
  const [idx, setIdx] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [sessionDone, setSessionDone] = useState(0);

  const loadStats = useCallback(() => {
    apiCall('/srs/stats', 'GET', null, token).then(setStats).catch(() => {});
  }, [token]);

  useEffect(() => {
    apiCall('/review/history', 'GET', null, token).then(setItems).catch(() => {});
    loadStats();
  }, [token, loadStats]);

  const startSrs = async () => {
    const rows = await apiCall('/srs/due', 'GET', null, token).catch(() => []);
    setDue(rows || []);
    setIdx(0); setRevealed(false); setSessionDone(0);
    setMode('srs');
  };

  const rate = async (quality) => {
    const card = due[idx];
    if (!card) return;
    await apiCall('/srs/review', 'POST', { id: card.id, quality }, token).catch(() => {});
    setSessionDone(n => n + 1);
    loadStats();
    if (idx < due.length - 1) { setIdx(idx + 1); setRevealed(false); }
    else { setMode('done'); }
  };

  const card = due[idx];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-3xl mx-auto px-4 py-8" data-testid="review-page">
      <button onClick={mode === 'home' ? onBack : () => setMode('home')} className="text-white/60 hover:text-white text-sm mb-4 flex items-center gap-1"><ChevronLeft size={16} />{mode === 'home' ? 'Retour' : 'Révisions'}</button>

      {mode === 'home' && (
        <>
          <div className="flex items-center gap-2 mb-2"><RotateCcw size={20} className="text-emerald-300" /><h2 className="text-2xl font-bold">Révisions</h2></div>
          <p className="text-white/60 text-sm mb-6">La répétition espacée te fait revoir chaque mot juste avant de l'oublier — c'est la clé pour retenir à vie.</p>

          <div className="grid grid-cols-3 gap-3 mb-5">
            <StatCard label="À réviser" value={stats.due} accent="yellow" testid="srs-stat-due" />
            <StatCard label="Total mots" value={stats.total} accent="emerald" testid="srs-stat-total" />
            <StatCard label="Maîtrisés" value={stats.learned} accent="green" testid="srs-stat-learned" />
          </div>

          <button onClick={startSrs} disabled={stats.due === 0} data-testid="srs-start"
            className="w-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-slate-950 font-bold py-3 rounded-xl flex items-center justify-center gap-2 mb-8">
            <Sparkles size={16} />{stats.due > 0 ? `Réviser ${stats.due} mot${stats.due > 1 ? 's' : ''}` : 'Rien à réviser pour le moment'}
          </button>
          {stats.total === 0 && <p className="text-xs text-white/40 -mt-6 mb-8 text-center">Termine une leçon pour remplir ta liste de révision.</p>}

          <div className="flex items-center gap-2 mb-3"><BookOpen size={16} className="text-emerald-300" /><h3 className="font-bold">Relire mes leçons récentes</h3></div>
          {items.length === 0 ? (
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center text-white/50 text-sm">Aucune leçon récente.</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {items.map(l => (
                <button key={l.id} onClick={() => onOpenLesson(l)} data-testid={`review-${l.id}`}
                  className="text-left bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl p-4 transition">
                  <div className="flex items-center justify-between"><span className="text-xs text-emerald-300 font-bold">{l.level}</span><span className="text-[10px] text-white/40">{l.module_id}</span></div>
                  <div className="font-bold mt-2">{l.title}</div>
                  <div className="mt-3 text-xs text-white/50 flex items-center gap-2"><RotateCcw size={12} />Rejouer</div>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {mode === 'srs' && card && (
        <div data-testid="srs-session">
          <div className="text-xs text-white/50 mb-4 text-center">{idx + 1} / {due.length}</div>
          <div className="h-1.5 bg-white/10 rounded-full mb-6 overflow-hidden">
            <div className="h-full bg-emerald-400 transition-all" style={{ width: `${(idx / due.length) * 100}%` }} />
          </div>
          <div onClick={() => setRevealed(true)}
            className="bg-gradient-to-br from-emerald-900/40 to-slate-900 border border-white/10 rounded-3xl p-10 text-center min-h-[220px] flex flex-col items-center justify-center cursor-pointer" data-testid="srs-card">
            <div className="text-3xl font-black">{card.src}</div>
            <button onClick={(e) => { e.stopPropagation(); speak(card.src, 'en-US'); }} className="mt-3 p-2 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30" data-testid="srs-listen"><Volume2 size={16} className="text-emerald-300" /></button>
            <AnimatePresence>
              {revealed && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-5 pt-5 border-t border-white/10 w-full">
                  <div className="text-xl text-yellow-200 font-semibold" data-testid="srs-answer">{card.tgt}</div>
                </motion.div>
              )}
            </AnimatePresence>
            {!revealed && <div className="text-xs text-white/40 mt-4">Touche la carte pour voir la réponse</div>}
          </div>

          {revealed ? (
            <div className="grid grid-cols-4 gap-2 mt-6">
              <RateBtn label="À revoir" color="red" onClick={() => rate(0)} testid="srs-rate-0" />
              <RateBtn label="Difficile" color="amber" onClick={() => rate(1)} testid="srs-rate-1" />
              <RateBtn label="Correct" color="emerald" onClick={() => rate(2)} testid="srs-rate-2" />
              <RateBtn label="Facile" color="green" onClick={() => rate(3)} testid="srs-rate-3" />
            </div>
          ) : (
            <button onClick={() => setRevealed(true)} data-testid="srs-reveal" className="w-full mt-6 bg-white/10 hover:bg-white/20 py-3 rounded-xl font-bold">Voir la réponse</button>
          )}
        </div>
      )}

      {mode === 'done' && (
        <div className="text-center py-12" data-testid="srs-done">
          <CheckCircle2 size={56} className="text-emerald-400 mx-auto mb-4" />
          <h2 className="text-2xl font-black text-emerald-300">Session terminée !</h2>
          <p className="text-white/70 mt-2">{sessionDone} mot{sessionDone > 1 ? 's' : ''} révisé{sessionDone > 1 ? 's' : ''}. Reviens demain pour ancrer davantage.</p>
          <button onClick={() => setMode('home')} className="mt-6 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-6 py-3 rounded-xl" data-testid="srs-back-home">Terminer</button>
        </div>
      )}
    </motion.div>
  );
}

function StatCard({ label, value, accent, testid }) {
  const c = { yellow: 'text-yellow-300', emerald: 'text-emerald-300', green: 'text-green-300' }[accent] || '';
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-3 text-center" data-testid={testid}>
      <div className={`text-2xl font-black ${c}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-white/50 mt-0.5">{label}</div>
    </div>
  );
}

function RateBtn({ label, color, onClick, testid }) {
  const c = {
    red: 'bg-red-500/20 hover:bg-red-500/30 text-red-200 border-red-400/30',
    amber: 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border-amber-400/30',
    emerald: 'bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border-emerald-400/30',
    green: 'bg-green-500/25 hover:bg-green-500/35 text-green-100 border-green-400/40',
  }[color];
  return (
    <button onClick={onClick} data-testid={testid} className={`border rounded-xl py-3 text-xs font-bold ${c}`}>{label}</button>
  );
}
